import React, { useState } from "react";

import {
  Button,
  Modal,
  MultiTypeDownloadToolTip,
} from "@king-ict/eupisi-ui/components";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { useTypedSelector } from "../app/rootReducer";
import { useAppDispatch } from "../app/store";
import { SaveResult } from "../models";
import styles from "./Toolbar.module.scss";
import { resetIsEdited, setSaveOK } from "../slices/applicationSlice";
import { getSaveCatchValidationFailures } from "../utils";

export interface ToolbarEditProps {
  children?: any;
  showDownload?: "hide" | "show";
  showDelete?: "hide" | "disable" | "show";
  deleteDisabledReason?: string;
  showSave?: "hide" | "disable" | "show";
  showCancel?: "hide" | "disable" | "show";
  showCustom?: boolean;
  isDataEdited?: boolean;
  leftContent?: React.ReactNode;
  onClickDownload?: any;
  onClickChangeFormat?: any;
  handleSave?: () => Promise<SaveResult<any>>;
  redirectUrlSaveOK?: (id: any) => string;
  setSaveErrors?: any;
  saveText?: string;
  onClickSave?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickDelete?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onClickCancel?: (event: React.MouseEvent<any, MouseEvent>) => void;
  onSaveSuccess?: (data: SaveResult<any>) => void;
  hasSchoolAttendanceNote?: {
    visible?: boolean;
    outline?: boolean;
    redirectUrl: string;
  };
}

export const ToolbarEdit = ({
  children,
  showSave,
  showCancel,
  showDownload,
  showDelete,
  deleteDisabledReason,
  showCustom,
  isDataEdited,
  leftContent,
  onClickDownload,
  onClickChangeFormat,
  handleSave,
  redirectUrlSaveOK,
  setSaveErrors,
  saveText,
  onClickSave,
  onClickDelete,
  onClickCancel,
  onSaveSuccess,
  hasSchoolAttendanceNote,
}: ToolbarEditProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [exportType, setExportType] = useState<number>(ExportTypeEnum.Excel);

  const dispatch = useAppDispatch();
  const isEdited: boolean = useTypedSelector(
    (state) => state.application.isEdited
  );

  /// ------ EVENTS
  const handleChangeFormat = (typeId: number) => {
    setExportType(typeId);
    onClickChangeFormat(typeId);
  };
  const onClickSaveBtn = (e: any) => {
    if (!handleSave) {
      onClickSave?.(e);
      return;
    }
    handleSave?.()
      .then((result) => {
        if (result && result.result !== undefined) {
          if (result.isValid) {
            dispatch(resetIsEdited());
            dispatch(setSaveOK(t("common.saveAndRefreshSuccess")));

            // Call onSaveSuccess before any navigation
            onSaveSuccess?.(result);

            if (redirectUrlSaveOK) {
              history.replace({
                pathname: redirectUrlSaveOK(
                  result.result?.id ||
                    result.result?.institutionId ||
                    result.result
                ),
              });
            }
          }
          setSaveErrors(result.validationFailures);
        } else {
          setSaveErrors([
            {
              propertyName: "000",
              message: t("common.systemError"),
            },
          ]);
        }
      })
      .catch((error) => {
        console.error("Save error:", error);
        setSaveErrors(getSaveCatchValidationFailures(error, t));
      });
  };
  /// ------ RENDER
  const DownloadButton = () => {
    switch (showDownload) {
      case "show":
        return (
          <MultiTypeDownloadToolTip
            withOverlay={true}
            exportType={exportType}
            onclick={onClickDownload}
            changeFormat={handleChangeFormat}
          ></MultiTypeDownloadToolTip>
        );
      default:
        return <></>;
    }
  };

  const generateSchoolAttendanceNote = () => {
    if (!hasSchoolAttendanceNote?.visible) return;

    return (
      <Button
        outline={!hasSchoolAttendanceNote?.outline}
        size="sm"
        onClick={() => {
          history.push(hasSchoolAttendanceNote.redirectUrl);
        }}
      >
        {t("schoolAttendance.title")}
      </Button>
    );
  };

  const SaveButton = () => {
    if (showSave === "hide") return <></>;
    else if (showSave === "show" && (isEdited || isDataEdited))
      return (
        <Button
          className="mr-1 float-right"
          outline={false}
          size="sm"
          onClick={onClickSaveBtn}
        >
          {saveText ?? t("common.save")}
        </Button>
      );
    else if (showSave === "disable" || !isEdited || !isDataEdited)
      return (
        <Button
          className="mr-1 float-right"
          outline={false}
          size="sm"
          disabled={true}
        >
          {saveText ?? t("common.save")}
        </Button>
      );
    else return <></>;
  };
  const CancelButton = () => {
    switch (showCancel) {
      case "show":
        return (
          <>
            <Button
              className="mr-1 float-right"
              outline={true}
              size="sm"
              onClick={onClickCancel ? onClickCancel : () => history.goBack()}
            >
              {t("common.quit")}
            </Button>
          </>
        );
      case "disable":
        return (
          <Button
            className="mr-1 float-right"
            outline={true}
            size="sm"
            disabled={true}
          >
            {t("common.quit")}
          </Button>
        );
      default:
        return <></>;
    }
  };
  const DeleteButton = () => {
    switch (showDelete) {
      case "show":
        return (
          <>
            <Button
              className="mr-1 float-right"
              color="danger"
              outline={true}
              size="sm"
              onClick={() => setShowDeleteModal(true)}
            >
              {t("common.delete")}
            </Button>
            <Modal
              type={"confirm"}
              title={t("common.deleteModalTitle")}
              cancelText={t("common.no")}
              confirmText={t("common.yes")}
              open={showDeleteModal}
              onClose={() => setShowDeleteModal(false)}
              onConfirm={onClickDelete}
            >
              {t("common.deleteModalMessage")}
            </Modal>
          </>
        );
      case "disable":
        return (
          // A disabled button receives no mouse events, so the tooltip explaining
          // why deleting is not possible has to sit on the wrapper.
          <span
            className="mr-1 float-right d-inline-block"
            title={deleteDisabledReason}
          >
            <Button color="danger" outline={true} size="sm" disabled={true}>
              {t("common.delete")}
            </Button>
          </span>
        );
      default:
        return <></>;
    }
  };
  const CustomButton = () => (showCustom ? <>{children}</> : <></>);

  return (
    <>
      <div className={styles.toolbar}>
        <Row>
          <Col lg="3">
            <div className="float-left">{leftContent}</div>
            {DownloadButton()}
            {generateSchoolAttendanceNote()}
          </Col>
          <Col lg="3" />
          <Col lg="6">
            {CustomButton()}
            {SaveButton()}
            {DeleteButton()}
            {CancelButton()}
          </Col>
        </Row>
      </div>
    </>
  );
};
